import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';

import moment from 'moment';

import type { BlockThemeConfig, DChartInterface, DSSetup, Service } from 'webapp-types';
import ThemeConfig from 'webapp/const/theme-config';
import { BillingFeature, BlockVisualization, TableColumnType } from 'webapp/enums/enums';
import { AppEvent } from 'webapp/enums/events';
import { TrackingEvent } from 'webapp/enums/tracking';
import { hexToRgba } from 'webapp/utils/color';

import BlockContentAiSummaryComponent from 'webapp/components/block/content/ai-summary';
import BlockContentChartComponent from 'webapp/components/block/content/chart';
import BlockContentCommonDisabledOverlayComponent from 'webapp/components/block/content/common/disabled-overlay';
import BlockContentCommonTitleComponent from 'webapp/components/block/content/common/title';
import BlockContentDataStoryComponent from 'webapp/components/block/content/data-story';
import BlockContentEmptyComponent from 'webapp/components/block/content/empty';
import BlockContentImageComponent from 'webapp/components/block/content/image';
import BlockContentTableComponent from 'webapp/components/block/content/table';
import BlockContentTextComponent from 'webapp/components/block/content/text';
import BlockContentTopLineComponent from 'webapp/components/block/content/top-line';
import BlockContentTopNumbersComponent from 'webapp/components/block/content/top-numbers';

import type BlockRenderer from 'webapp/models-custom/block-renderer';
import type { ChangeVisualizationWithPossibleConfirmFnReturnType } from 'webapp/models-custom/block-setup';
import type BlockState from 'webapp/models-custom/block-state';
import type { SettingsChanges as BlockStateSettingsChanges } from 'webapp/models-custom/block-state';
import type { DataPerformanceSummaryObject } from 'webapp/models-custom/data-performance-summary/block';

interface Args {
	blockState: BlockState;
	blockRenderer: BlockRenderer;
	isWallView: boolean;
	canEdit: boolean;
	canChangeDateRange: boolean;
	canChangeFilter: boolean;
	canChangeVisualization: boolean;
	canAnnotate: boolean;
	isUnselectable: boolean;
	colorConfig: BlockThemeConfig.Parsed;
	dataPerformanceSummaryObject: DataPerformanceSummaryObject | null;
	skipMasterFilter?: boolean;
}

export default class BlockContentComponent extends Component<Args> {
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('model-manager/blocks') declare mmBlocksService: Service.ModelManager.Blocks;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('billing') declare billingService: Service.Billing;
	@service('model-manager/block-previews')
	declare mmBlockPreviewsService: Service.ModelManager.BlockPreviews;
	@service('model-manager/data-selects')
	declare mmDataSelectService: Service.ModelManager.DataSelects;
	@service('metric-raw-data') declare metricRawDataService: Service.MetricRawData;
	@service('account') declare accountService: Service.Account;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;
	@service('metric-details') declare metricDetailsService: Service.MetricDetails;
	@service('popup') declare popupService: Service.Popup;

	get block() {
		return this.args.blockState.block;
	}

	get blockSetup() {
		return this.block.blockSetup;
	}

	get visualization() {
		return this.blockSetup.visualization;
	}

	get isEmpty() {
		return (
			this.block.isDataEmpty &&
			!this.block.isSyncing &&
			!this.blockSetup.visualization.isDataStory
		);
	}

	get canEditName() {
		return this.args.canEdit && !this.blockSetup.isTemporaryBlock;
	}

	get onlyCompatibleVisualizations() {
		return (
			this.args.isUnselectable &&
			!this.block.isFromDataCalculation &&
			!this.block.isFromCustomQuery
		);
	}

	@cached
	get limitationsOverlayFeature() {
		return this.block.showLimitationsOverlay ? this.block.limitationFeature : null;
	}

	@cached
	get themeData(): BlockThemeConfig.BlockParsed {
		const colorConfigBase = this.args.colorConfig;

		const themeConfig = ThemeConfig.find((item) => item.id === colorConfigBase.themeId)!;

		const colorConfig: BlockThemeConfig.BlockParsed = Object.assign({}, colorConfigBase, {
			visualizationConfig: this.visualization.getBlockThemeConfigContent(
				themeConfig.visualizations,
			),
			colors: themeConfig.colors,
		});

		return colorConfig;
	}

	get textBlockLinkColor() {
		const data = this.themeData;
		return data.colors[data.visualizationConfig.link!];
	}

	get style() {
		const styles: string[] = [];
		const opacitiesNeeded = [0.1, 0.2];
		Object.entries(this.blockSetup.customColorClassMap).forEach((entry) => {
			const [hex, customClass] = entry;
			styles.push(`--${customClass}: ${hex};`);
			opacitiesNeeded.forEach((opacity) => {
				const rgba = hexToRgba(hex, opacity);
				styles.push(
					`--${customClass}-alpha-${String(Math.round(opacity * 100))}: ${rgba};`,
				);
			});
			styles.push(...this.getChartCustomColorVariablesForCustomClass(customClass));
		});
		return htmlSafe(styles.join(''));
	}

	private getChartCustomColorVariablesForCustomClass(customColorClass: string) {
		// with multiple blocks that use custom-color-0 (with different hex each),
		// we face an issue of first rendered block's svg LinearGradient def being used for all blocks
		// (due to them having the same def id which is then referenced in css url())
		// only url() are problematic because they must reference ids (which must be unique)

		// Example issue:
		// e.g. block1 custom-color-0 is green, block2 custom-color-0 is red.
		// both share css fill: url("#gradient-area-custom-color-0");
		// but the actual def style is read from the **first rendered** bcos of the same def ids
		// so we get custom color from block1 bleeding over to block2

		// to solve this, we generate css variables (unique per custom color)
		// with the unique (per custom color **per block**) url() values (contain block id)
		// this variable is used in css, which correctly maps to each block's def

		// make sure when updating here, update also highcharts.less (.generate-custom-colors helper)
		// and renderers that actually output defs with their ids.
		// And vice versa.

		// possible improvement - only output those that are necessary for current visualization

		const variableChunks = [
			'gradient-area',
			'gradient-area-stacked',
			'gradient-area-light',
			'gradient-area-very-light',
			'gradient-area-stacked-light',
			'pattern-bar-missing',
			'gradient-funnel-main-series',
			'gradient-pipeline-main-series',
		];

		return variableChunks.map((chunk) => {
			return `--svg-url-${chunk}-${customColorClass}: url("#${chunk}-${this.block.id}-${customColorClass}");`;
		});
	}

	get isInMetricInsight() {
		return this.metricDetailsService.isMetricInsightsTemporaryId(
			this.block.numericId?.toString(),
		);
	}

	get isInGoalInsight() {
		return this.block.isFromGoalInsights;
	}

	get showTopLine() {
		const isBlockPopupPreviewOpen = this.block.isShownInBlockPreview;

		return !this.isInMetricInsight && !this.isInGoalInsight && !isBlockPopupPreviewOpen;
	}

	get showBlockTitle() {
		return !this.isInMetricInsight && !this.isInGoalInsight;
	}

	private lastMouseDownTs = 0; // ms

	@action
	handleBlockContentEvent(e: MouseEvent) {
		// this is the logic that expands / opens rightside panel settings on content click
		// the relevant clickable items (table cells, chart lines etc.)
		// must have "action-target" class & data-dsid attribute defined

		if (this.args.isUnselectable) {
			return;
		}

		const isClick = e.type === 'click',
			isMouseDown = e.type === 'mousedown';

		if (!isClick && !isMouseDown) {
			return;
		}

		if (isMouseDown) {
			this.lastMouseDownTs = moment.now();
			return;
		}

		const notDragLengthMs = 500;
		if (this.lastMouseDownTs && moment.now() - this.lastMouseDownTs > notDragLengthMs) {
			// assume drag, bcos takes too long between mousedown and click
			this.lastMouseDownTs = 0;
			return;
		}

		const dsId = this.getDataSelectId(e);

		if (dsId) {
			this.eventManagerService.trigger(AppEvent.BLOCK_CONTENT_CLICKED, { dsId });
		}
	}

	@action
	changeVisualization(
		toVisualizationType: BlockVisualization,
	): ChangeVisualizationWithPossibleConfirmFnReturnType {
		if (this.block.isFromReport) {
			// on reports, if visualization is not available, instead of actually switching
			// and immediately covering it in (report-specific) an limitation overlay,
			// we want to show limitation popup
			const limitation = this.block.getLimitationForVisualization(toVisualizationType);
			if (limitation) {
				const actionPassesLimitations = this.billingService.checkIfCan(limitation);

				if (!actionPassesLimitations) {
					const trackingOrigin =
						limitation === BillingFeature.GENERATIVE_AI
							? 'generative_ai_block'
							: undefined;

					this.billingService.handleLimitations(limitation, trackingOrigin);
					return null;
				}
			}
		}

		if (this.args.isUnselectable) {
			if (this.args.canChangeVisualization) {
				// changing into preview, no confirm needed, bcos only compatible types are offered

				const settings: BlockStateSettingsChanges = {
					visualization: toVisualizationType,
				};

				// extra change when switching visualization to table (setting tableColumnType)
				// for more info, see mmBlockPreviewsService.getTableBlocksThatMustSwitchToAutoOnMasterDimensionsChange
				const blockState = this.args.blockState;
				const dimensions =
					blockState.changes.metricSetup?.dimensions ||
					blockState.boardState.selectedMasterDimensions ||
					null;

				const mustUpdateTableColumnType =
					this.mmBlockPreviewsService.getTableBlocksThatMustSwitchToAutoOnMasterDimensionsChange(
						[this.block],
						dimensions,
						true,
					).length > 0;

				if (mustUpdateTableColumnType) {
					settings.tableColumnType = TableColumnType.AUTO;
				}

				this.args.blockState.updateSettings(settings);
				if (toVisualizationType === BlockVisualization.AI_SUMMARY) {
					this.mmBlocksService.checkToMarkBlockVisualizationUsed(this.block, true);
				}
			}
			return null;
		} else {
			// in designer - save directly
			this.analyticsService.event(TrackingEvent.BLOCK_VISUALIZATION_CHANGED, {
				origin: `datablock_${this.block.trackingOrigin}`,
				visualization:
					this.mmBlocksService.getVisualizationNameFromVisualizationType(
						toVisualizationType,
					),
			});
			return this.blockSetup.changeVisualizationWithPossibleConfirm(toVisualizationType);
		}
	}

	@action
	changeDimensionsFilter(
		dimensions: string[] | null | undefined,
		filters?: DSSetup.MetricSetupFiltersData | null,
	) {
		// dimensions null means reset

		// extra change for table blocks when switching master dimensions (tableColumnType)
		// for more info, see mmBlockPreviewsService.getTableBlocksThatMustSwitchToAutoOnMasterDimensionsChange
		const mustUpdateTableColumnType =
			this.mmBlockPreviewsService.getTableBlocksThatMustSwitchToAutoOnMasterDimensionsChange(
				[this.block],
				dimensions || null,
			).length > 0;

		this.args.blockState.updateMetricSetup(
			dimensions,
			filters || null,
			mustUpdateTableColumnType,
		);
		if (mustUpdateTableColumnType) {
			this.args.blockState.updateTableColumnTypeSetting(TableColumnType.AUTO);
		}
	}

	private getDataSelectId(e: MouseEvent) {
		let dsId;
		const pointOnChart = (
			(e as Highcharts.PointClickEventObject).point as
				DChartInterface.ChartHoverPoint | undefined
		)?.dbxPoint;
		if (pointOnChart) {
			dsId = pointOnChart.dsId;
		} else if (
			e.detail &&
			typeof e.detail === 'object' &&
			(e.detail as { dsId?: number }).dsId
		) {
			// we passed the details explicitly (e.g. gauge click)
			dsId = (e.detail as { dsId?: number }).dsId;
		} else {
			// else read from DOM
			const target = (e.target as HTMLElement)?.closest('.action-target');
			dsId = +(target?.getAttribute('data-dsid') || '');
		}

		return dsId;
	}
	<template>
		<div
			class="block-content {{if this.limitationsOverlayFeature 'with-disabled-overlay'}}"
			style={{this.style}}
		>

			{{#if this.block.isText}}
				<BlockContentTextComponent
					@blockState={{@blockState}}
					@themeData={{this.themeData}}
					@isUnselectable={{@isUnselectable}}
				/>
			{{else if this.block.isImage}}
				<BlockContentImageComponent
					@blockState={{@blockState}}
					@blockRenderer={{@blockRenderer}}
					@canEdit={{@canEdit}}
					@isUnselectable={{@isUnselectable}}
					@theme={{this.themeData.bgTheme}}
				/>
			{{else}}

				{{#if this.showTopLine}}
					<BlockContentTopLineComponent
						@blockState={{@blockState}}
						@blockRenderer={{@blockRenderer}}
						@onlyCompatibleVisualizations={{this.onlyCompatibleVisualizations}}
						@canChangeDateRange={{@canChangeDateRange}}
						@canChangeFilter={{@canChangeFilter}}
						@canChangeVisualization={{@canChangeVisualization}}
						@skipMasterFilter={{@skipMasterFilter}}
						@changeVisualization={{this.changeVisualization}}
						@changeDimensionsFilter={{this.changeDimensionsFilter}}
						@dataPerformanceSummaryObject={{@dataPerformanceSummaryObject}}
						@canEdit={{@canEdit}}
					/>
				{{/if}}

				<div
					class="block-content__main"
					role="button"
					{{! not really, just to shut up the linter }}
					{{on "mousedown" this.handleBlockContentEvent}}
				>

					{{#if this.showBlockTitle}}
						<BlockContentCommonTitleComponent
							@blockState={{@blockState}}
							@blockRenderer={{@blockRenderer}}
							@canEdit={{this.canEditName}}
						/>
					{{/if}}

					{{#if this.isEmpty}}

						<BlockContentEmptyComponent
							@blockState={{@blockState}}
							@blockRenderer={{@blockRenderer}}
							@isUnselectable={{@isUnselectable}}
						/>

					{{else if this.visualization.isTable}}

						<BlockContentTableComponent
							@blockState={{@blockState}}
							@blockRenderer={{@blockRenderer}}
							@stickyHeaderColor={{this.themeData.bgColor}}
							@lazyloadRows={{@isWallView}}
						/>

					{{else if this.visualization.isDataStory}}

						<BlockContentDataStoryComponent
							@blockState={{@blockState}}
							@blockRenderer={{@blockRenderer}}
							@themeData={{this.themeData}}
							@isUnselectable={{@isUnselectable}}
							@canEdit={{@canEdit}}
						/>

					{{else if this.visualization.isAiSummary}}

						<BlockContentAiSummaryComponent @blockState={{@blockState}} />

					{{else}}

						{{#if this.visualization.canRenderTopNumbers}}
							<BlockContentTopNumbersComponent
								@blockState={{@blockState}}
								@blockRenderer={{@blockRenderer}}
								@themeData={{this.themeData}}
							/>
						{{/if}}

						{{#if this.visualization.canRenderChart}}
							<BlockContentChartComponent
								@blockState={{@blockState}}
								@blockRenderer={{@blockRenderer}}
								@themeData={{this.themeData}}
								@canAnnotate={{@canAnnotate}}
							/>
						{{/if}}

					{{/if}}

				</div>

			{{/if}}

			{{#if this.limitationsOverlayFeature}}
				<BlockContentCommonDisabledOverlayComponent
					@feature={{this.limitationsOverlayFeature}}
					@bgColor={{this.themeData.bgColor}}
				/>
			{{/if}}

		</div>
	</template>
}
